import { css } from "@emotion/react";
import React, { useEffect, useState } from "react";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { getLocationCortar } from "@src/hooks/newFilter/useGetLocation";
import { useFilterTextList, useFloorList } from "@src/pages/mapFilter/useFilterList";
import type { FavoriteRegionSlot } from "../store/favoriteRegionStore";

async function getRegionDisplayName(loc: string): Promise<string> {
    const sidoText = await getLocationCortar("0000000000").then((res: any[]) => {
        const selected = res?.filter((val: any) => val?.cortarNo?.slice(0, 2) === loc.slice(0, 2));
        return selected?.[0]?.cortarName ?? "";
    });
    if (loc.slice(2) === "00000000") return `${sidoText} > 전체`;
    const sigunguText = await getLocationCortar(`${loc.slice(0, 2)}00000000`).then((res: any[]) => {
        const selected = res?.filter((val: any) => val?.cortarNo?.slice(0, 5) === loc.slice(0, 5));
        return selected?.[0]?.cortarName ?? "";
    });
    if (loc.slice(5) === "00000") return `${sidoText} > ${sigunguText} > 전체`;
    const emdText = await getLocationCortar(`${loc.slice(0, 5)}00000`).then((res: any[]) => {
        const selected = res?.filter((val: any) => val?.cortarNo === loc);
        return selected?.[0]?.cortarName ?? "전체";
    });
    return `${sidoText} > ${sigunguText} > ${emdText}`;
}

interface Props {
    slot: FavoriteRegionSlot | undefined;
    onEdit?: () => void;
    onDelete?: () => void;
}

const SelectedRegionSummary: React.FC<Props> = ({ slot, onEdit, onDelete }) => {
    const [regionNames, setRegionNames] = useState<string[]>([]);

    const isFinish = slot?.isFinish ?? false;
    const regionCodes = slot?.regions?.filter((r) => r !== "all") ?? [];
    const hasRegions = regionCodes.length > 0;
    const categoryLabel =
        slot?.categories?.[0] && (useFilterTextList.find((t) => t.value === slot.categories![0])?.name ?? slot.categories![0]);
    const floorValue = slot?.floor;
    const floorLabel =
        floorValue != null && slot?.categories?.[0] === "D02"
            ? useFloorList.find((f) => parseInt(f.value, 10) === floorValue)?.name ?? ""
            : "";
    const showSummary = isFinish && (hasRegions || categoryLabel);

    useEffect(() => {
        if (regionCodes.length === 0) {
            setRegionNames([]);
            return;
        }
        let cancelled = false;
        const resolve = async () => {
            const names = await Promise.all(regionCodes.map((loc) => getRegionDisplayName(loc)));
            if (!cancelled) setRegionNames(names);
        };
        resolve();
        return () => {
            cancelled = true;
        };
    }, [regionCodes.join(",")]);

    if (!showSummary) return null;

    return (
        <div css={summaryStyle}>
            <div className="summary-content">
                {hasRegions && (
                    <div className="row">
                        <span className="label">소재지</span>
                        <span className="value">{regionNames.length > 0 ? regionNames.join(", ") : "로딩 중..."}</span>
                    </div>
                )}

                {categoryLabel && (
                    <div className="row">
                        <span className="label">용도</span>
                        <span className="value">
                            {categoryLabel}
                            {floorLabel ? ` (${floorLabel.replace(/\n/g, " ")})` : ""}
                        </span>
                    </div>
                )}
            </div>
            <div css={buttonGroupStyle}>
                {onEdit && (
                    <button type="button" css={iconButtonStyle} onClick={onEdit} aria-label="수정">
                        <EditRoundedIcon sx={{ fontSize: 20 }} />
                    </button>
                )}
                <button
                    type="button"
                    css={[iconButtonStyle, deleteButtonStyle]}
                    onClick={onDelete}
                    aria-label="삭제"
                    disabled={!onDelete}
                >
                    <DeleteOutlinedIcon sx={{ fontSize: 20 }} />
                </button>
            </div>
        </div>
    );
};

export default SelectedRegionSummary;

const summaryStyle = css`
    padding: 12px 16px;
    margin: 12px 14px 28px;
    background: #f5f7fa;
    border-radius: 12px;
    border: 1px solid #e8ecf1;
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-direction: row;

    .summary-content {
        display: flex;
        align-items: start;
        justify-content: space-between;
        flex-direction: column;
        gap: 6px;
    }
    .row {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        margin-bottom: 6px;
        &:last-child {
            margin-bottom: 0;
        }
    }
    .label {
        color: #666;
        flex-shrink: 0;
        min-width: 40px;
    }
    .value {
        color: #0c002c;
        font-weight: 500;
        word-break: break-all;
    }
`;

const buttonGroupStyle = css`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const iconButtonStyle = css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    color: #0047ff;
    background: #fff;
    border: 1px solid #e8ecf1;
    border-radius: 10px;
    cursor: pointer;

    &:disabled {
        color: #c5c5c5;
        cursor: default;
    }
`;

const deleteButtonStyle = css`
    color: #e53935;
    border: 1px solid #e8ecf1;

    &:disabled {
        color: #c5c5c5;
        border-color: #e8ecf1;
    }
`;
